import { DestroyRef, Directive, EventEmitter, inject, Input, OnInit, Output } from "@angular/core";
import {
  AbstractControl,
  FormArray,
  FormGroup,
  FormGroupDirective,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup
} from "@angular/forms";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Field } from "../../../../domain/dynamic-form-model";
import { WebsocketService } from "../../../../services/websocket.service";
import { FormControlService } from "../../../../services/form-control.service";
import { cloneDeep, isEqual } from 'lodash';

interface PositionChange {
  oldIndex: number;
  newIndex: number;
  element: HTMLElement;
}

@Directive({
  standalone: true
})
export abstract class BaseFieldComponent implements OnInit {
  protected destroyRef = inject(DestroyRef);
  protected rootFormGroup = inject(FormGroupDirective);

  private formControlService = inject(FormControlService);
  private wsService = inject(WebsocketService);

  @Input() fieldData: Field;
  @Input() editMode: boolean;
  @Input() readonly: boolean = null;
  @Input() position?: number = null;
  @Input() scrollContainer: HTMLElement | null = null;

  @Output() hasChanges = new EventEmitter<boolean>();

  formControl!: UntypedFormControl;
  form!: UntypedFormGroup;
  previousValue!: any;
  inputId!: string;
  hideField: boolean = null;

  focused = false;

  ngOnInit() {
    if (this.position !== null) {
      this.form = this.rootFormGroup.control.controls[this.position] as UntypedFormGroup;
    } else {
      this.form = this.rootFormGroup.control;
    }
    // console.log(this.form);
    this.formControl = this.form.get(this.fieldData.name) as UntypedFormControl;
    this.inputId = this.getPath(this.formControl).join('.');

    if (this.fieldData.form.dependsOn) {
      // console.log(this.fieldData.form.dependsOn);
      this.enableDisableField(this.form.get(this.fieldData.form.dependsOn.name).value, this.fieldData.form.dependsOn.value);

      this.form.get(this.fieldData.form.dependsOn.name).valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
        value => {
          this.enableDisableField(value, this.fieldData.form.dependsOn.value);
        },
        error => {console.error(error)}
      );
    }
  }

  /** Field focus -------------------------------------------------------------------------------------------------> **/
  focus(position?: number) {
    if (!this.editMode)
      return;

    // console.log('Field focus In');
    this.focused = true;
    this.previousValue = cloneDeep(this.formControl.value);
    if (this.formControl instanceof FormArray) {
      this.wsService.WsFocus(this.getPath(this.formControl.controls[position]).join('.'), null);
    } else
      this.wsService.WsFocus(this.getPath(this.formControl).join('.'), null);
  }

  focusOut(skip: boolean, position?: number) {
    if (!this.editMode)
      return;

    // console.log('Field focus Out');
    this.focused = false;
    this.wsService.WsFocus(null, null);
    if (isEqual(this.previousValue, this.formControl.value) || skip)
      return;

    if (this.formControl instanceof FormArray) {
      // send a full array or single input?
      // this.wsService.WsEdit({field: this.getPath(this.formControl.controls[position]).join('.'), value: this.formControl.value});
      this.wsService.WsEdit({field: this.getPath(this.formControl.controls[position]).join('.'), value: this.formControl.controls[position].value});
    } else
      this.wsService.WsEdit({field: this.getPath(this.formControl).join('.'), value: this.formControl.value});
  }

  // For fields made of several focusable parts (editor + toolbar, date input + calendar): moving focus between
  // the parts is not a real focus out, so the other users keep seeing the field as active.
  containerFocusOut(event: FocusEvent, skip: boolean, position?: number) {
    if ((event.currentTarget as HTMLElement).contains(event.relatedTarget as Node | null))
      return;

    this.focusOut(skip, position);
  }
  /** <------------------------------------------------------------------------------------------------- Field focus **/

  /** Form control path -------------------------------------------------------------------------------------------> **/
  getPath(control: AbstractControl): string[] {
    const path: string[] = [];
    let currentControl: AbstractControl | null = control;

    // Traverse up the tree until reaching the root
    while (currentControl && currentControl.parent) {
      if (currentControl.parent instanceof FormArray) {
        const index = '['+this.findIndexInFormArray(currentControl.parent, currentControl)+']';
        path.unshift(index.toString());
      } else {
        const parent = currentControl.parent;
        const index = this.findIndexInParent(parent, currentControl);
        path.unshift(index);
      }
      currentControl = currentControl.parent;
      // console.log(path);
    }

    return path;
  }

  findIndexInParent(parent: FormGroup | FormArray, control: AbstractControl): string {
    const keys = Object.keys(parent.controls);
    for (let i = 0; i < keys.length; i++) {
      if (parent.controls[keys[i]] === control) {
        return keys[i];
      }
    }
    return '';
  }

  findIndexInFormArray(formArray: FormArray, control: AbstractControl): number {
    const index = formArray.controls.findIndex(c => c === control);
    return index >= 0 ? index : -1;
  }
  /** <------------------------------------------------------------------------------------------- Form control path **/

  /** Handle Arrays --> **/
  get formArrayControls() {
    // Ensure this returns the existing FormArray reference, do not use .filter() or .map() here
    return (this.form.get(this.fieldData.name) as FormArray).controls;
  }

  fieldAsFormArray() {
    return this.formControl as unknown as UntypedFormArray;
  }

  push() {
    this.fieldAsFormArray().push(this.formControlService.createField(this.fieldData), {emitEvent: false});
    if (this.editMode && this.formControl instanceof FormArray) {
      this.wsService.WsEdit({
        field: this.getPath(this.formControl.controls[this.fieldAsFormArray().length-1]).join('.'),
        value: this.formControl.controls[this.fieldAsFormArray().length-1].value,
        action: {type: 'ADD'}
      });
    }
  }

  remove(i: number) {

    let path;
    if (this.formControl instanceof FormArray)
      path = this.getPath(this.formControl.controls[i]).join('.');

    this.fieldAsFormArray().removeAt(i, {emitEvent: false});
    if (this.editMode && this.formControl instanceof FormArray) {
      this.wsService.WsEdit({
        field: path,
        value: null,
        action: {type: 'DELETE', index: i}
      });
    }
  }

  onPositionChanged(change: PositionChange): void {
    console.log(`Element ${change.element.id} moved from index ${change.oldIndex} to ${change.newIndex}`);
    this.move(change.newIndex, change.oldIndex)
  }

  move(newIndex: number, oldIndex: number) {
    const formArray: UntypedFormArray = this.fieldAsFormArray();
    const currentControl: AbstractControl = formArray.at(oldIndex);
    let path: string;
    if (this.formControl instanceof FormArray) {
      path = this.getPath(this.formControl.controls[oldIndex]).join('.');
    }
    // console.log(this.formControl);
    // console.log(path);

    formArray.removeAt(oldIndex, {emitEvent: false});
    formArray.insert(newIndex, currentControl, {emitEvent: false})

    if (this.editMode) {
      this.wsService.WsEdit({
        field: path,
        value: null,
        action: {type:'MOVE', index: newIndex}
      });
    }
  }

  /** check fields validity --> **/
  checkFormValidity(): boolean {
    if (this.readonly)
      return false;

    return (this.formControl.invalid && (this.formControl.touched || this.formControl.dirty));
  }

  checkFormArrayValidity(position: number): boolean {
    const formControl: AbstractControl = this.fieldAsFormArray()?.get([position]);
    // console.log(formControl);
    // console.log(position);
    if (formControl)
      return (!formControl.valid && (formControl.touched || formControl.dirty));
    else
      return false
  }

  /** Other -------------------------------------------------------------------------------------------------------> **/
  enableDisableField(value: string | number, enableValue: string | string[] | number[]) {

    if (enableValue === null ) { // Enable for any value functionality
      if (value !== null && value !== undefined && value.toString().trim() !== '') {
        this.formControl.enable();
        this.hideField = false;
      } else {
        this.formControl.disable();
        this.formControl.reset();
        this.hideField = true;
      }
      return;
    }

    if (enableValue instanceof Array) {
      const index = enableValue.findIndex((v: string | number) => v == value)
      if (index >= 0) {
        this.formControl.enable();
        this.hideField = false;
      } else {
        this.formControl.disable();
        this.formControl.reset();
        this.hideField = true;
      }
    } else if (value?.toString() == enableValue) {
      this.formControl.enable();
      this.hideField = false;
    } else {
      this.formControl.disable();
      this.formControl.reset();
      this.hideField = true;
      // maybe add this if the remaining empty fields are a problem
      // (this.formControl as unknown as FormArray).clear();
    }
  }

  clearControl() {
    this.formControl.reset(null);
  }

  unsavedChangesPrompt() {
    this.hasChanges.emit(true);
  }

  timeOut(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

}

